import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client";
import { getCourseDetailsByIdsQuery } from "../modules/CoursesQueries";
import { DEBOUNCE_MS } from "../modules/CoursesConstants";
import _ from "lodash";

/**
 * Phase 2 hook: Fetches course details (title, grades) for visible course IDs.
 * Uses debouncing and ref-based caching to avoid re-fetching.
 * When fetchAllIds is provided (non-empty), fetches all IDs at once (used during search).
 *
 * @param {string[]} visibleCourseIds - IDs of courses currently in the viewport
 * @param {boolean} viewOnly - If true, skips fetching
 * @param {string[]} [fetchAllIds] - When non-empty, fetch these IDs instead of visible ones (for search)
 * @returns {{ courseDetailsMap: Object, loading: boolean }}
 */
const useCourseDetails = (visibleCourseIds, viewOnly, fetchAllIds) => {
  const courseDetailsMapRef = useRef({});
  const previousCourseIdsRef = useRef([]);
  const [debouncedVisibleIds, setDebouncedVisibleIds] = useState(
    visibleCourseIds
  );
  const [preLoading, setPreLoading] = useState(false);
  const [detailsVersion, setDetailsVersion] = useState(0);

  // When fetchAllIds is provided, use those directly (no debounce needed for search)
  const isFetchAll = !_.isEmpty(fetchAllIds);

  // Debounce visible IDs to avoid excessive queries during scrolling
  useEffect(() => {
    if (isFetchAll) return; // Skip debounce when fetching all
    setPreLoading(true);
    const timeoutId = setTimeout(() => {
      setPreLoading(false);
      setDebouncedVisibleIds(visibleCourseIds);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [JSON.stringify(visibleCourseIds), isFetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter out already-cached IDs to avoid re-fetching
  const courseIdsToFetch = useMemo(() => {
    const sourceIds = isFetchAll ? fetchAllIds : debouncedVisibleIds;
    if (_.isEmpty(sourceIds)) return [];

    const idsToFetch = _.filter(
      sourceIds,
      id => !courseDetailsMapRef.current[id]
    );

    const sortedIds = _.sortBy(idsToFetch);

    if (_.isEqual(sortedIds, previousCourseIdsRef.current)) {
      return previousCourseIdsRef.current;
    }

    previousCourseIdsRef.current = sortedIds;
    return sortedIds;
  }, [debouncedVisibleIds, isFetchAll, fetchAllIds]);

  const { data, loading } = useQuery(getCourseDetailsByIdsQuery, {
    variables: { courseIds: courseIdsToFetch },
    context: { useHttpLink: true },
    skip: _.isEmpty(courseIdsToFetch) || viewOnly,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    errorPolicy: "all",
  });

  // Merge fetched details into the persistent ref
  useEffect(() => {
    const multiNodeData = _.get(data, "multiNode", []);

    if (_.isEmpty(multiNodeData)) return;

    let hasNewData = false;
    _.forEach(multiNodeData, course => {
      if (course.id && !courseDetailsMapRef.current[course.id]) {
        courseDetailsMapRef.current[course.id] = course;
        hasNewData = true;
      }
    });

    if (hasNewData) {
      setDetailsVersion(prev => prev + 1);
    }
  }, [data]);

  // Return a snapshot of the ref for consumers
  const courseDetailsMap = useMemo(() => ({ ...courseDetailsMapRef.current }), [
    detailsVersion,
  ]);

  return {
    courseDetailsMap,
    loading: (loading && _.isEmpty(data)) || preLoading,
  };
};

export default useCourseDetails;
